import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { UserProfile } from '../../models/authTypes';
import type { UserRole } from '../../models/authTypes';
import { deleteUser, deleteUserDocument } from '../../services/userService';
import { theme } from '../../theme';
import { FaTrash, FaEdit, FaUserShield } from 'react-icons/fa';

// Estilos
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 24px;
  color: ${theme.colors.text.primary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.small};
  overflow: hidden;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  background-color: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrastText};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.divider};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border: none;
  border-radius: ${theme.borderRadius.small};
  background-color: ${props => 
    props.$variant === 'danger' ? theme.colors.error.main :
    props.$variant === 'secondary' ? theme.colors.secondary.main :
    theme.colors.primary.main
  };
  color: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: ${theme.transitions.normal};
  margin-right: 8px;
  
  &:hover {
    background-color: ${props => 
      props.$variant === 'danger' ? theme.colors.error.dark :
      props.$variant === 'secondary' ? theme.colors.secondary.dark :
      theme.colors.primary.dark
    };
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:disabled {
    background-color: ${theme.colors.text.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const SearchBar = styled.div`
  display: flex;
  margin-bottom: 24px;
  
  input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid ${theme.colors.divider};
    border-radius: ${theme.borderRadius.small};
    font-size: ${theme.typography.sizes.md};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary.main};
      box-shadow: 0 0 0 2px ${theme.colors.primary.light};
    }
  }
`;

const UserAvatar = styled.div<{ $photoURL?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.colors.primary.light};
  background-image: ${props => props.$photoURL ? `url(${props.$photoURL})` : 'none'};
  background-size: cover;
  background-position: center;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary.contrastText};
  font-weight: ${theme.typography.fontWeights.bold};
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-weight: ${theme.typography.fontWeights.medium};
`;

const UserEmail = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text.secondary};
`;

const UserRole = styled.span<{ $role: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.fontWeights.medium};
  text-transform: uppercase;
  
  background-color: ${props => 
    props.$role === 'superadmin' ? theme.colors.error.light :
    props.$role === 'admin' ? theme.colors.warning.light :
    props.$role === 'comisario' ? theme.colors.info.light :
    theme.colors.success.light
  };
  
  color: ${props => 
    props.$role === 'superadmin' ? theme.colors.error.dark :
    props.$role === 'admin' ? theme.colors.warning.dark :
    props.$role === 'comisario' ? theme.colors.info.dark :
    theme.colors.success.dark
  };
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: ${theme.borderRadius.medium};
  width: 400px;
  max-width: 90%;
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 16px;
  color: ${theme.colors.text.primary};
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 32px;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.lg};
`;

const Alert = styled.div<{ $type: 'success' | 'error' | 'warning' | 'info' }>`
  padding: 16px;
  margin-bottom: 24px;
  border-radius: ${theme.borderRadius.medium};
  background-color: ${props => 
    props.$type === 'success' ? theme.colors.success.light :
    props.$type === 'error' ? theme.colors.error.light :
    props.$type === 'warning' ? theme.colors.warning.light :
    theme.colors.info.light
  };
  color: ${props => 
    props.$type === 'success' ? theme.colors.success.dark :
    props.$type === 'error' ? theme.colors.error.dark :
    props.$type === 'warning' ? theme.colors.warning.dark :
    theme.colors.info.dark
  };
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    font-size: 20px;
  }
`;

// Componente principal
const UsersManager: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info', message: string } | null>(null);
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Cargar usuarios
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersQuery = query(collection(db, 'users'), orderBy('lastLogin', 'desc'));
        const querySnapshot = await getDocs(usersQuery);
        
        const usersData: UserProfile[] = [];
        querySnapshot.forEach((doc) => {
          usersData.push({ ...doc.data(), uid: doc.id } as UserProfile);
        });
        
        setUsers(usersData);
        setFilteredUsers(usersData);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar usuarios:', err);
        setError('Error al cargar la lista de usuarios. Por favor, inténtalo de nuevo más tarde.');
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  // Filtrar usuarios cuando cambia el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);
  
  // Formatear fecha
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (err) {
      return 'Fecha inválida';
    }
  };
  
  // Obtener iniciales del nombre
  const getInitials = (name: string) => {
    if (!name) return '?';
    
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };
  
  // Mostrar modal de confirmación para eliminar usuario
  const handleDeleteClick = (user: UserProfile) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };
  
  // Cancelar eliminación
  const handleCancelDelete = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };
  
  // Confirmar eliminación de usuario
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      
      // Intentar eliminar el usuario usando la Cloud Function
      const result = await deleteUser(userToDelete.uid);
      
      if (result.success) {
        // Actualizar la lista de usuarios
        setUsers(prevUsers => prevUsers.filter(user => user.uid !== userToDelete.uid));
        setAlertMessage({
          type: 'success',
          message: `El usuario ${userToDelete.displayName || userToDelete.email} ha sido eliminado correctamente.`
        });
      } else {
        // Si falla, mostrar mensaje de error
        setAlertMessage({
          type: 'error',
          message: `Error al eliminar el usuario: ${result.message}`
        });
        
        // Intentar eliminar solo el documento de Firestore como alternativa
        if (window.confirm('No se pudo eliminar el usuario de Authentication. ¿Deseas eliminar solo el documento de usuario de la base de datos?')) {
          const docResult = await deleteUserDocument(userToDelete.uid);
          if (docResult.success) {
            setUsers(prevUsers => prevUsers.filter(user => user.uid !== userToDelete.uid));
            setAlertMessage({
              type: 'warning',
              message: docResult.message
            });
          } else {
            setAlertMessage({
              type: 'error',
              message: `Error al eliminar el documento: ${docResult.message}`
            });
          }
        }
      }
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      setAlertMessage({
        type: 'error',
        message: `Error inesperado: ${err instanceof Error ? err.message : 'Error desconocido'}`
      });
    } finally {
      setUserToDelete(null);
      setShowDeleteModal(false);
      setLoading(false);
    }
  };
  
  // Verificar si el usuario actual puede eliminar a otro usuario
  const canDeleteUser = (user: UserProfile) => {
    if (!currentUser) return false;
    
    // No permitir eliminar al propio usuario
    if (user.uid === currentUser.uid) return false;
    
    // Solo superadmin puede eliminar a otros superadmins
    if (user.role === 'superadmin' && currentUser.role !== 'superadmin') return false;
    
    // Superadmin puede eliminar a cualquiera
    if (currentUser.role === 'superadmin') return true;
    
    // Admin puede eliminar a usuarios normales y comisarios
    if (currentUser.role === 'admin' && (user.role === 'usuario' || user.role === 'comisario')) return true;
    
    return false;
  };
  
  // Traducir rol a español
  const translateRole = (role: string): string => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Administrador';
      case 'comisario': return 'Comisario';
      case 'usuario': return 'Usuario';
      default: return role;
    }
  };
  
  // Si el usuario no tiene permisos, redirigir a la página de inicio
  if (currentUser?.role !== 'superadmin' && currentUser?.role !== 'admin') {
    return (
      <Container>
        <Title>Administración de Usuarios</Title>
        <Alert $type="error">
          <FaUserShield />
          No tienes permisos para acceder a esta página. Esta página está restringida a administradores.
        </Alert>
        <Button onClick={() => navigate('/home')}>Volver al inicio</Button>
      </Container>
    );
  }
  
  return (
    <Container>
      <Title>Administración de Usuarios</Title>
      
      {alertMessage && (
        <Alert $type={alertMessage.type}>
          {alertMessage.type === 'success' && <FaUserShield />}
          {alertMessage.type === 'error' && <FaTrash />}
          {alertMessage.type === 'warning' && <FaUserShield />}
          {alertMessage.message}
        </Alert>
      )}
      
      <SearchBar>
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchBar>
      
      {loading ? (
        <p>Cargando usuarios...</p>
      ) : error ? (
        <Alert $type="error">
          <FaUserShield />
          {error}
        </Alert>
      ) : filteredUsers.length === 0 ? (
        <NoResults>
          No se encontraron usuarios que coincidan con la búsqueda.
        </NoResults>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Usuario</Th>
              <Th>Rol</Th>
              <Th>Último acceso</Th>
              <Th>Fecha de registro</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.uid}>
                <Td>
                  <UserCell>
                    <UserAvatar $photoURL={user.photoURL}>
                      {!user.photoURL && getInitials(user.displayName || '')}
                    </UserAvatar>
                    <UserInfo>
                      <UserName>{user.displayName || 'Sin nombre'}</UserName>
                      <UserEmail>{user.email}</UserEmail>
                    </UserInfo>
                  </UserCell>
                </Td>
                <Td>
                  <UserRole $role={user.role}>
                    {translateRole(user.role)}
                  </UserRole>
                </Td>
                <Td>{formatDate(user.lastLogin || '')}</Td>
                <Td>{formatDate(user.createdAt)}</Td>
                <Td>
                  <Button
                    $variant="danger"
                    onClick={() => handleDeleteClick(user)}
                    disabled={!canDeleteUser(user)}
                    title={!canDeleteUser(user) ? 'No tienes permisos para eliminar este usuario' : 'Eliminar usuario'}
                  >
                    <FaTrash /> Eliminar
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      
      {showDeleteModal && userToDelete && (
        <Modal>
          <ModalContent>
            <ModalTitle>Confirmar eliminación</ModalTitle>
            <p>¿Estás seguro de que deseas eliminar al usuario <strong>{userToDelete.displayName || userToDelete.email}</strong>?</p>
            <p>Esta acción eliminará al usuario de Firebase Authentication y todos sus datos asociados. No se puede deshacer.</p>
            <ModalButtons>
              <Button onClick={handleCancelDelete}>Cancelar</Button>
              <Button $variant="danger" onClick={handleConfirmDelete}>
                <FaTrash /> Eliminar
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default UsersManager;
